import { useState } from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  mergeReminderIntervals,
  parseReminderIntervalInput,
} from '@/lib/invoiceSettings'

interface Props {
  value: number[]
  onChange: (value: number[]) => void
  readOnly?: boolean
  placeholder?: string
  className?: string
  inputClassName?: string
  error?: string
}

export function ReminderIntervalsInput({
  value,
  onChange,
  readOnly = false,
  placeholder,
  className,
  inputClassName,
  error,
}: Props) {
  const [draft, setDraft] = useState('')

  const addValues = (raw: string) => {
    const next = parseReminderIntervalInput(raw)
    if (next.length === 0) return
    onChange(mergeReminderIntervals(value, next))
    setDraft('')
  }

  const remove = (day: number) => {
    onChange(value.filter(v => v !== day))
  }

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 && (
        <div className='flex flex-wrap gap-3'>
          {value.map(day => (
            <span
              key={day}
              className='inline-flex h-8 w-[61px] items-center justify-center gap-2 rounded-[8px] border border-[#e7e9ef] bg-white text-sm font-medium leading-none text-[#171a20] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_4px_8px_-4px_rgba(16,24,40,0.16)]'
            >
              {day}
              {!readOnly && (
                <button
                  type='button'
                  className='inline-flex size-4 items-center justify-center rounded-full text-[#171a20] transition-colors hover:bg-[#f4f5f8]'
                  onClick={() => remove(day)}
                  aria-label={`Remove ${day}`}
                >
                  <X className='size-4' />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <Input
        value={draft}
        placeholder={placeholder}
        readOnly={readOnly}
        className={cn(
          inputClassName,
          readOnly && 'bg-muted/40',
          error && 'border-destructive'
        )}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            addValues(draft)
          }
        }}
        onBlur={() => {
          if (draft.trim()) addValues(draft)
        }}
      />
      {error && <p className='text-sm text-destructive'>{error}</p>}
    </div>
  )
}
